import { klona } from 'klona'
import { conditionalHtmlToLiquid } from '@dashboard/components/common/liquidConditionals'
import { funnelDefinitionEach } from '@dashboard/modules/flow/services/funnelDefinitions'
import type { NodeSg } from '@dashboard/modules/flow/builder/types/nodes'
import { useQuestionStore } from '@dashboard/modules/segment/stores/questionStore'
import { useSegmentGroupStore } from '@dashboard/modules/segment/stores/segmentGroupStore'
import { experienceLabel, toSentence } from '@dashboard/services/string'
import { idPrefixes, makeId } from '@dashboard/services/uuid'
import { useConfigStore } from '@dashboard/stores/configStore'
import { dimensionSchema, questionSchema, type Config, type Dimension, type Question } from '@dashboard/types/config'
import type { QuestionDraft } from './questionCatalog'
import { resolveQuestionId } from './questionNavigation'
import { logicDefinitionsEach } from './segmentUtilities'

export function getQuestionName(question: Question, dimension?: Dimension | null): string {
  return experienceLabel(dimension?.name || question.name || question.text)
}

/** Persist only at the explicit Create/Add action; local drafts never share references with config. */
export function createQuestion(draft: QuestionDraft): QuestionDraft {
  const config = useConfigStore().config
  if (!config) {
    throw new Error('Load the project before creating a question')
  }
  // Zod passthrough condition/display payloads retain nested references, so clone before parsing.
  const local = klona(draft)
  const question = questionSchema.parse(local.question)
  const dimension = local.dimension ? dimensionSchema.parse(local.dimension) : null
  if (question.dimensionId !== (dimension?.id ?? null)) {
    throw new Error('The question and its answer group must match')
  }
  if (
    config.rightAsk.questions.some((existing) => existing.id === question.id) ||
    (dimension && config.dimensions.some((existing) => existing.id === dimension.id))
  ) {
    throw new Error('This question has already been created')
  }
  const questionStore = useQuestionStore()
  if (!dimension) {
    return { question: questionStore.createInputQuestion(question), dimension: null }
  }
  const createdDimension = useSegmentGroupStore().create({ type: 'question', segmentGroup: dimension, question })
  const createdQuestion = questionStore.getById(question.id)
  if (!createdQuestion) {
    throw new Error('The created question could not be found')
  }
  return { question: createdQuestion, dimension: createdDimension }
}

type ReferenceNode = {
  $source?: string
  $type?: string
  dimensionId?: string
  dimension_id?: string
  segmentGroupId?: string
  questionId?: string | string[]
  segment?: string
  segment_id?: string
  segmentId?: string | string[]
  and?: ReferenceNode[]
  or?: ReferenceNode[]
  not?: ReferenceNode
}

/** Remap references, not user-entered text, provider field IDs, score names, or score amounts. */
function remapConditions(value: ReferenceNode | undefined, ids: ReadonlyMap<string, string>): void {
  if (!value) {
    return
  }
  if (value.$source === 'segments') {
    if (typeof value.$type === 'string') {
      value.$type = ids.get(value.$type) ?? value.$type
    }
    if (typeof value.segmentId === 'string') {
      value.segmentId = ids.get(value.segmentId) ?? value.segmentId
    } else if (Array.isArray(value.segmentId)) {
      value.segmentId = value.segmentId.map((id) => ids.get(id) ?? id)
    }
  }
  if (value.$source === 'answers' || (value.$source === 'scores' && value.$type === 'inputQuestion')) {
    if (typeof value.questionId === 'string') {
      value.questionId = ids.get(value.questionId) ?? value.questionId
    } else if (Array.isArray(value.questionId)) {
      value.questionId = value.questionId.map((id) => ids.get(id) ?? id)
    }
  }
  for (const key of ['and', 'or'] as const) {
    const children = value[key]
    if (Array.isArray(children)) {
      children.forEach((child) => remapConditions(child, ids))
    }
  }
  remapConditions(value.not, ids)
}
function remapDimensionReferences(dimension: Dimension, ids: ReadonlyMap<string, string>): void {
  if (dimension.syncMappings) {
    dimension.syncMappings = Object.fromEntries(
      Object.entries(dimension.syncMappings).map(([id, destination]) => [ids.get(id) ?? id, destination])
    )
  }
  for (const signal of dimension.signals) {
    signal.indicates = ids.get(signal.indicates) ?? signal.indicates
    remapConditions(signal.definition, ids)
  }
}

/** Segment-only counterpart; linked questions must be copied together with their dimension. */
export function duplicateSegmentGroup(dimensionId: string): Dimension {
  const config = useConfigStore().config
  const source = config?.dimensions.find((dimension) => dimension.id === dimensionId)
  if (!config || !source) {
    throw new Error('This segment group could not be found')
  }
  if (config.rightAsk.questions.some((question) => question.dimensionId === dimensionId)) {
    throw new Error('Use Duplicate question to copy this question and its answers together')
  }
  const dimension = klona(source)
  const occupied = config.dimensions.flatMap((group) => [group.id, ...group.segments.map((segment) => segment.id)])
  const ids = new Map<string, string>()
  dimension.id = makeId(idPrefixes.DIMENSION, occupied)
  occupied.push(dimension.id)
  ids.set(source.id, dimension.id)
  dimension.name = `${experienceLabel(source.name)} (copy)`
  for (const segment of dimension.segments) {
    const newId = makeId(idPrefixes.SEGMENT, occupied)
    occupied.push(newId)
    ids.set(segment.id, newId)
    segment.id = newId
  }
  remapDimensionReferences(dimension, ids)
  return useSegmentGroupStore().create({ type: 'segmentgroup', segmentGroup: dimension })
}

function duplicateWithReferences(questionId: string): { draft: QuestionDraft; ids: Map<string, string> } {
  const config = useConfigStore().config
  const canonicalId = resolveQuestionId(config, questionId)
  const source = config?.rightAsk.questions.find((question) => question.id === canonicalId)
  if (!config || !source) {
    throw new Error('This question could not be found')
  }
  const sourceDimension = source.dimensionId
    ? config.dimensions.find((dimension) => dimension.id === source.dimensionId)
    : null
  if (source.dimensionId && !sourceDimension) {
    throw new Error('The question’s answer group could not be found')
  }
  const question = klona(source)
  const dimension = sourceDimension ? klona(sourceDimension) : null
  const ids = new Map<string, string>()
  const occupied = [
    ...config.rightAsk.questions.flatMap((item) => [item.id, ...item.options.map((option) => option.id)]),
    ...config.dimensions.flatMap((item) => [item.id, ...item.segments.map((segment) => segment.id)])
  ]
  const replaceId = (id: string, prefix: string) => {
    const replacement = makeId(prefix, occupied)
    occupied.push(replacement)
    ids.set(id, replacement)
    return replacement
  }
  question.id = replaceId(source.id, idPrefixes.RIGHT_ASK_QUESTION)
  const name = `${getQuestionName(source, sourceDimension)} (copy)`
  if (dimension) {
    dimension.id = replaceId(dimension.id, idPrefixes.DIMENSION)
    dimension.name = name
    for (const segment of dimension.segments) {
      segment.id = replaceId(segment.id, idPrefixes.SEGMENT)
    }
    question.dimensionId = dimension.id
    remapDimensionReferences(dimension, ids)
  } else {
    question.name = name
  }
  for (const option of question.options) {
    option.id = replaceId(option.id, idPrefixes.RIGHT_ASK_QUESTION_OPTION)
    option.segmentId = ids.get(option.segmentId) ?? option.segmentId
    remapConditions(option.conditions, ids)
  }
  return { draft: createQuestion({ question, dimension }), ids }
}

/** A library copy has no flow membership and no SQL split test. */
export function duplicateQuestion(questionId: string): QuestionDraft {
  return duplicateWithReferences(questionId).draft
}

/** Only the selected node's immediate branch keys change; descendants and other nodes stay shared as before. */
export function copyIntoFlow(node: NodeSg): QuestionDraft {
  if (!node.segmentGroupId) {
    throw new Error('Select a question before making a copy')
  }
  const { draft, ids } = duplicateWithReferences(node.segmentGroupId)
  node.segmentGroupId = draft.dimension?.id ?? draft.question.id
  if (node.split) {
    for (const branch of node.split) {
      if (branch.segmentId) {
        branch.segmentId = ids.get(branch.segmentId) ?? branch.segmentId
      }
    }
  }
  return draft
}

export interface QuestionUse {
  id: string
  name: string
}
export interface QuestionFlowUse extends QuestionUse {
  asks: boolean
  branches: boolean
  routes: boolean
  condition: boolean
  setsAnswer: boolean
  label: string
}
export interface QuestionUses {
  flows: QuestionFlowUse[]
  offers: QuestionUse[]
  campaigns: QuestionUse[]
  widgets: QuestionUse[]
  emails: QuestionUse[]
  segmentGroups: QuestionUse[]
  questions: QuestionUse[]
  phrases: QuestionUse[]
}
export interface QuestionEmail {
  pid: string | number
  name: string
  original_email?: string | null
}
export interface QuestionUsesOptions {
  /** Emails are SQL resources, not part of config. Pass the personalized email store's loaded rows. */
  emails?: readonly QuestionEmail[]
  /** Resolved provider Liquid tokens for this question's mapped fields (field IDs need not equal tokens). */
  emailFieldTokens?: readonly string[]
}

type ReferenceTarget = {
  questionId: string | null
  dimensionId: string | null
  segmentIds: Set<string>
  answersOnly?: boolean
}

function referencesQuestion(input: unknown, target: ReferenceTarget): boolean {
  const value = input as ReferenceNode | unknown[] | null | undefined
  if (Array.isArray(value)) {
    return value.some((child: unknown) => referencesQuestion(child, target))
  }
  if (!value || typeof value !== 'object') {
    return false
  }
  if (target.answersOnly) {
    return (
      (value.$source === 'answers' && value.questionId === target.questionId) ||
      Object.values(value).some((child) => referencesQuestion(child, target))
    )
  }
  if (
    target.dimensionId &&
    (value.dimensionId === target.dimensionId ||
      value.dimension_id === target.dimensionId ||
      value.segmentGroupId === target.dimensionId ||
      (value.$source === 'segments' && value.$type === target.dimensionId))
  ) {
    return true
  }
  if (
    target.questionId &&
    (value.questionId === target.questionId ||
      (Array.isArray(value.questionId) && value.questionId.includes(target.questionId)))
  ) {
    return true
  }
  for (const key of ['segment', 'segmentId', 'segment_id'] as const) {
    const id = value[key]
    if (typeof id === 'string' && target.segmentIds.has(id)) {
      return true
    }
    if (Array.isArray(id) && id.some((item: unknown) => typeof item === 'string' && target.segmentIds.has(item))) {
      return true
    }
  }
  return Object.values(value).some((child) => typeof child === 'object' && referencesQuestion(child, target))
}

export function getQuestionUses(config: Config, questionId: string, options: QuestionUsesOptions = {}): QuestionUses {
  const canonicalId = resolveQuestionId(config, questionId)
  const question = config.rightAsk.questions.find((item) => item.id === canonicalId)
  return getUses(
    config,
    question,
    config.dimensions.find((item) => item.id === question?.dimensionId),
    options
  )
}

export function getSegmentGroupUses(
  config: Config,
  dimensionId: string,
  options: QuestionUsesOptions = {}
): QuestionUses {
  const dimension = config.dimensions.find((item) => item.id === dimensionId)
  const question = config.rightAsk.questions.find((item) => item.dimensionId === dimensionId)
  return getUses(config, question, dimension, options)
}

function getUses(
  config: Config,
  question: Question | undefined,
  dimension: Dimension | undefined,
  options: QuestionUsesOptions,
  answersOnly = false
): QuestionUses {
  const uses: QuestionUses = {
    flows: [],
    offers: [],
    campaigns: [],
    widgets: [],
    emails: [],
    segmentGroups: [],
    questions: [],
    phrases: []
  }
  if (!question && !dimension) {
    return uses
  }
  const target: ReferenceTarget = {
    questionId: question?.id ?? null,
    dimensionId: dimension?.id ?? null,
    segmentIds: new Set(dimension?.segments.map((segment) => segment.id)),
    answersOnly
  }
  for (const flow of config.rightCta?.offerFunnels ?? []) {
    const usage: QuestionFlowUse = {
      id: flow.id,
      name: flow.name,
      asks: false,
      branches: false,
      routes: false,
      condition: false,
      setsAnswer: false,
      label: ''
    }
    funnelDefinitionEach(flow.funnelDefinition, (node) => {
      if (
        !answersOnly &&
        node.type === 'sg' &&
        ((question && node.segmentGroupId === question.id) ||
          (target.dimensionId && node.segmentGroupId === target.dimensionId))
      ) {
        usage.asks ||= node.ask
        usage.routes ||= !node.ask
        usage.branches ||=
          !!node.split && node.split.some((branch) => !!branch.segmentId && target.segmentIds.has(branch.segmentId))
      }
      if (node.type === 'yesno' && referencesQuestion(node.condition, target)) {
        usage.condition = true
      }
      if (!answersOnly && node.type === 'data' && referencesQuestion(node.actions, target)) {
        usage.setsAnswer = true
      }
    })
    if (answersOnly) {
      usage.condition = referencesQuestion(flow, target)
    }
    if (usage.asks || usage.routes || usage.branches || usage.condition || usage.setsAnswer) {
      usage.label = usage.asks
        ? usage.branches
          ? 'Asks and branches'
          : 'Asks'
        : usage.routes || usage.branches
          ? 'Routes on it'
          : usage.condition
            ? 'Condition'
            : 'Sets an answer'
      uses.flows.push(usage)
    }
  }
  for (const offer of config.rightCta?.offers ?? []) {
    if (
      !answersOnly &&
      'fields' in offer.triggeredAction.settings &&
      offer.triggeredAction.settings.fields?.some(
        (field) =>
          field.displayType === 'sg' &&
          ((question && field.segmentGroupId === question.id) || (dimension && field.segmentGroupId === dimension.id))
      )
    ) {
      uses.offers.push({ id: offer.id, name: offer.name })
    }
  }
  for (const campaign of config.campaigns) {
    if (referencesQuestion(campaign, target)) {
      uses.campaigns.push({ id: campaign.id, name: campaign.name })
    }
  }
  for (const widget of config.rightCta?.widgets ?? []) {
    if (referencesQuestion(answersOnly ? widget : widget.visibility?.conditions, target)) {
      uses.widgets.push({ id: widget.id, name: widget.name })
    }
  }
  for (const group of config.dimensions) {
    if (
      (!answersOnly && group.id === dimension?.id) ||
      !referencesQuestion(answersOnly ? group : group.signals.map((signal) => signal.definition), target)
    ) {
      continue
    }
    const linkedQuestion = config.rightAsk.questions.find((item) => item.dimensionId === group.id)
    if (linkedQuestion) {
      uses.questions.push({ id: linkedQuestion.id, name: getQuestionName(linkedQuestion, group) })
    } else {
      uses.segmentGroups.push({ id: group.id, name: group.name })
    }
  }
  for (const other of config.rightAsk.questions) {
    if (
      (answersOnly || other.id !== question?.id) &&
      referencesQuestion(answersOnly ? other : other.options.map((option) => option.conditions), target) &&
      !uses.questions.some((use) => use.id === other.id)
    ) {
      uses.questions.push({
        id: other.id,
        name: getQuestionName(
          other,
          config.dimensions.find((group) => group.id === other.dimensionId)
        )
      })
    }
  }
  for (const phrase of config.phrases ?? []) {
    if (!answersOnly && target.dimensionId && phrase.segmentGroupId === target.dimensionId) {
      uses.phrases.push({ id: phrase.id, name: experienceLabel(phrase.fallbackText) || 'Personalized phrase' })
    }
  }
  const fieldTokens = new Set(options.emailFieldTokens)
  // Provider tokens are often the stored field name. Numeric IDs need the caller's resolved catalog tokens.
  for (const field of [
    dimension?.syncToCustomField,
    question?.syncToField,
    question?.otherOptions?.syncToCustomField,
    ...Object.values(question?.inputSettings?.syncFieldMap ?? {})
  ]) {
    const token = field?.replace(/^(?:native|custom)\*/, '')
    if (token && /^[a-zA-Z_][\w.]*$/.test(token)) {
      fieldTokens.add(token)
    }
  }
  const fieldSuffixes = [...fieldTokens].map((field) => `.${field}`)
  const phraseIds = new Set(uses.phrases.map((phrase) => phrase.id))
  for (const email of answersOnly ? [] : (options.emails ?? [])) {
    const liquid = conditionalHtmlToLiquid(email.original_email ?? '')
    const expressions = liquid.match(/\{(?:\{[\s\S]*?\}|%[\s\S]*?%)\}/g) ?? []
    const referenced = expressions.some((expression) => {
      const withoutStrings = expression.replace(/"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g, '')
      const tokens = withoutStrings.match(/[a-zA-Z_][\w]*(?:\.[a-zA-Z_][\w]*)*/g) ?? []
      return tokens.some(
        (token) =>
          fieldTokens.has(token) ||
          fieldSuffixes.some((suffix) => token.endsWith(suffix)) ||
          (token.startsWith('phrases.') && phraseIds.has(token.slice('phrases.'.length)))
      )
    })
    if (referenced) {
      uses.emails.push({ id: String(email.pid), name: email.name })
    }
  }
  for (const group of Object.values(uses)) {
    group.sort((left: QuestionUse, right: QuestionUse) => left.name.localeCompare(right.name))
  }
  return uses
}

/** Remove linked controls, not the form itself or unrelated provider fields. */
export function removeQuestionFormFields(config: Config, ids: readonly string[]): void {
  for (const offer of config.rightCta?.offers ?? []) {
    const settings = offer.triggeredAction.settings
    if ('fields' in settings && settings.fields) {
      settings.fields = settings.fields.filter(
        (field) => field.displayType !== 'sg' || !field.segmentGroupId || !ids.includes(field.segmentGroupId)
      )
    }
  }
}

export interface QuestionDeleteImpact {
  removed: QuestionUses
  retained: QuestionUses
  blockedBy: QuestionUses
  message: string
}

/** Describe the existing store cascade, not an aspirational broader cleanup. Responses are never deleted. */
export function getQuestionDeleteImpact(
  config: Config,
  questionId: string,
  options: QuestionUsesOptions = {}
): QuestionDeleteImpact {
  const canonicalId = resolveQuestionId(config, questionId)
  const question = config.rightAsk.questions.find((item) => item.id === canonicalId)
  return getDeleteImpact(
    config,
    question,
    config.dimensions.find((item) => item.id === question?.dimensionId),
    options
  )
}

export function getSegmentGroupDeleteImpact(
  config: Config,
  dimensionId: string,
  options: QuestionUsesOptions = {}
): QuestionDeleteImpact {
  const dimension = config.dimensions.find((item) => item.id === dimensionId)
  const question = config.rightAsk.questions.find((item) => item.dimensionId === dimensionId)
  return getDeleteImpact(config, question, dimension, options)
}

function getDeleteImpact(
  config: Config,
  question: Question | undefined,
  dimension: Dimension | undefined,
  options: QuestionUsesOptions
): QuestionDeleteImpact {
  const uses = getUses(config, question, dimension, options)
  const blockedBy = getUses(config, question, dimension, {}, true)
  const blockingPlaces = Object.entries(blockedBy).flatMap(([kind, items]: [string, QuestionUse[]]) =>
    items.map((item) => `${kind === 'segmentGroups' ? 'Segment group' : kind.slice(0, -1)} "${item.name}"`)
  )
  const dimensionId = dimension?.id
  const removed: QuestionUses = {
    flows: [],
    offers: uses.offers,
    campaigns: [],
    widgets: [],
    emails: [],
    segmentGroups: [],
    questions: [],
    phrases: []
  }
  const retained: QuestionUses = { ...uses }
  retained.offers = []
  if (dimensionId) {
    const remainingFlows = klona(config.rightCta?.offerFunnels ?? [])
    const changedFlows = new Set<string>()
    for (const flow of remainingFlows) {
      funnelDefinitionEach(flow.funnelDefinition, (node) => {
        if (node.type === 'sg' && node.segmentGroupId === dimensionId) {
          changedFlows.add(flow.id)
          node.segmentGroupId = null
          node.split = false
        }
        if (node.type === 'data') {
          node.actions = node.actions.filter((action) => {
            if (action.type === 'set_segment' && action.segmentGroupId === dimensionId) {
              changedFlows.add(flow.id)
              return false
            }
            return true
          })
        }
      })
    }
    removed.flows = uses.flows.filter((flow) => changedFlows.has(flow.id))
    retained.flows = getUses(
      { ...config, rightCta: { ...config.rightCta, offerFunnels: remainingFlows } },
      question,
      dimension,
      {}
    ).flows
    removed.campaigns = uses.campaigns.filter((use) =>
      config.campaigns.some((campaign) => campaign.id === use.id && campaign.dimension_id === dimensionId)
    )
    retained.campaigns = uses.campaigns.filter(
      (use) => !removed.campaigns.some((removedUse) => removedUse.id === use.id)
    )
    removed.phrases = uses.phrases
    retained.phrases = []
    // destroy() visits the first two levels of signal OR/AND groups, but not NOT/deeper nesting or option conditions.
    const splitRuleUses = (items: QuestionUse[], isQuestion: boolean) => {
      for (const use of items) {
        const linked = isQuestion ? config.rightAsk.questions.find((item) => item.id === use.id) : undefined
        const group = config.dimensions.find((item) => item.id === (isQuestion ? linked?.dimensionId : use.id))
        let removedRule = false
        const remaining = group ? klona(group) : undefined
        if (remaining) {
          logicDefinitionsEach(remaining, (condition: ReferenceNode, nuke: () => void) => {
            if (condition.$source === 'segments' && condition.$type === dimensionId) {
              removedRule = true
              nuke()
            }
          })
        }
        const key = isQuestion ? 'questions' : 'segmentGroups'
        if (removedRule) {
          removed[key].push(use)
          const target = {
            questionId: question?.id ?? null,
            dimensionId,
            segmentIds: new Set(dimension?.segments.map((segment) => segment.id))
          }
          if (
            !referencesQuestion(
              remaining?.signals.map((signal) => signal.definition),
              target
            ) &&
            !referencesQuestion(
              linked?.options.map((option) => option.conditions),
              target
            )
          ) {
            retained[key] = retained[key].filter((item) => item.id !== use.id)
          }
        }
      }
    }
    splitRuleUses(uses.segmentGroups, false)
    splitRuleUses(uses.questions, true)
  }
  const removedParts = [
    removed.flows.length ? `${removed.flows.length} ${removed.flows.length === 1 ? 'flow' : 'flows'}` : '',
    removed.campaigns.length
      ? `${removed.campaigns.length} ${removed.campaigns.length === 1 ? 'campaign' : 'campaigns'}`
      : '',
    removed.phrases.length
      ? `${removed.phrases.length} personalized ${removed.phrases.length === 1 ? 'phrase' : 'phrases'}`
      : '',
    removed.segmentGroups.length + removed.questions.length
      ? `${removed.segmentGroups.length + removed.questions.length} ${removed.segmentGroups.length + removed.questions.length === 1 ? 'group’s rules' : 'groups’ rules'}`
      : ''
  ].filter(Boolean)
  const retainedParts = [
    retained.flows.length ? `${retained.flows.length} ${retained.flows.length === 1 ? 'flow' : 'flows'}` : '',
    retained.campaigns.length
      ? `${retained.campaigns.length} ${retained.campaigns.length === 1 ? 'campaign' : 'campaigns'}`
      : '',
    retained.widgets.length
      ? `${retained.widgets.length} ${retained.widgets.length === 1 ? 'widget’s targeting' : 'widgets’ targeting'}`
      : '',
    retained.emails.length ? `${retained.emails.length} ${retained.emails.length === 1 ? 'email' : 'emails'}` : '',
    retained.segmentGroups.length + retained.questions.length
      ? `${retained.segmentGroups.length + retained.questions.length} ${retained.segmentGroups.length + retained.questions.length === 1 ? 'group’s rules' : 'groups’ rules'}`
      : ''
  ].filter(Boolean)
  const message = [
    removedParts.length
      ? `Removes it from ${toSentence(removedParts)}.`
      : `Removes this ${question ? 'question' : 'segment group'} from your library.`,
    removed.offers.length
      ? `The linked question field will be removed from ${toSentence(removed.offers.map((offer) => `"${offer.name}"`))}.`
      : '',
    retainedParts.length ? `References in ${toSentence(retainedParts)} are not removed.` : '',
    question ? 'Past responses stay in your reports.' : 'Past data stays in your reports.'
  ]
    .filter(Boolean)
    .join(' ')
  return {
    removed,
    retained,
    blockedBy,
    message: blockingPlaces.length
      ? `Cannot delete this question because previous-answer conditions reference it in ${toSentence(blockingPlaces)}. Remove those conditions first.`
      : message
  }
}
